/**
 * Web3Auth v11 options
 *
 * Everything the v11 `Web3Auth` constructor needs, split out of the gateway so the
 * gateway can hold only behaviour. Values come from the environment and from
 * `environment.ts`'s browser detection; nothing here talks to the SDK.
 *
 * `chainConfig` and a provider instance are deliberately absent — in v11 the chain
 * comes from the dashboard project behind the client id, not from application code.
 */
import {
  CONNECTOR_INITIAL_AUTHENTICATION_MODE,
  MFA_LEVELS,
  WEB3AUTH_NETWORK
} from '@web3auth/modal';
import type { Web3AuthOptions, WEB3AUTH_NETWORK_TYPE } from '@web3auth/modal';

import { isBrowserSupportingPopupUxMode } from './environment';

const clientId = import.meta.env.VITE_WEB3AUTH_CLIENT_ID ?? '';

// `web3AuthNetwork` is an INPUT to wallet key derivation — every existing user's
// address depends on this value staying whatever the environment says it is.
// Never remap, coerce, or validate it; the only allowed fallback is the SDK's own
// devnet default for local/unset environments. The cast asserts shape, not value —
// whatever string the environment supplies passes through untouched.
const web3AuthNetwork = (import.meta.env.VITE_WEB3AUTH_NETWORK ??
  WEB3AUTH_NETWORK.SAPPHIRE_DEVNET) as WEB3AUTH_NETWORK_TYPE;

// Unlike `clientId` and `web3AuthNetwork` above, `appUrl` is NOT a derivation input —
// it is `WhiteLabelData`, the SDK's branding block, and varying it per deployment
// cannot move an existing user's address. So it follows the deployment instead of
// being frozen to production: Vercel gives every preview a per-deployment hostname
// that no build-time variable can know, which is why the origin is the fallback
// rather than the exception. Set VITE_APP_URL only to override branding with a
// canonical URL. Trailing slash stripped so the logo URLs below never double up.
const appUrl = (import.meta.env.VITE_APP_URL ?? window.location.origin).replace(/\/+$/, '');

export const web3AuthOptions: Web3AuthOptions = {
  clientId,
  web3AuthNetwork,
  // Applies once, at options level, instead of being repeated on every connect call.
  mfaLevel: MFA_LEVELS.NONE,
  // v11 defaults to CONNECT_AND_SIGN, which folds the auth-token fetch into connect().
  // That fetch reaches the citadel, which 404s on a genuinely-first login — with the
  // default, that 404 would take the whole connect down even though the session was
  // created successfully. CONNECT_ONLY keeps the two apart so 33-05's retry can
  // handle the citadel lag without failing the login itself. Do not remove.
  initialAuthenticationMode: CONNECTOR_INITIAL_AUTHENTICATION_MODE.CONNECT_ONLY,
  uiConfig: {
    // v11 defaults to 'popup'. Safari and every in-app browser (Facebook, Instagram,
    // TikTok, Line, Android WebView, iOS WKWebView) block popups outright, and Google
    // refuses OAuth from embedded webviews entirely. Every mobile browser redirects too:
    // a mobile popup tab is lost when Facebook hands off to its app. Dropping this
    // silently breaks login for most of LUDOKA's mobile traffic. Must keep reading
    // `environment.ts`.
    uxMode: isBrowserSupportingPopupUxMode ? 'popup' : 'redirect',
    // White-label branding: application `uiConfig` wins over dashboard branding, so
    // this stays in code — moving it to the dashboard would be a visible regression.
    appName: 'LUDOKA',
    appUrl,
    // `/logo.png` was never shipped in `public/` — it 404s in production, so the
    // login modal rendered a broken image. `pwa-512x512.png` is the asset that exists.
    logoLight: `${appUrl}/pwa-512x512.png`,
    logoDark: `${appUrl}/pwa-512x512.png`,
    defaultLanguage: 'fr',
    mode: 'dark',
    theme: { primary: '#00B4FF' }
  }
};
